import { AlertTriangle } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { describeGraphicFailure } from '@/features/graphics/graphic-failure';
import { useArtifactPanel } from '../artifact-panel-context';
import { ArtifactRow } from '../artifact-row';
import {
  type CreativeRef,
  creativeSlides,
  useCreativeAsset,
} from '../use-creative-asset';
import { type AdPreviewProps, describeAdStatus } from './ad-preview-card';
import { useSavedAdPreview } from './use-saved-ad-preview';

/**
 * Which rendered asset an ad is waiting on, if any.
 *
 * A library upload (`assetImageUrl` / `assetVideoUrl`) is already a file, so
 * there is nothing to poll. A legacy `videoId` can hold an image asset id; the
 * resolved direct URL wins over it, which is why the direct check comes first.
 */
export function adCreativeToPoll(
  ad: Pick<
    AdPreviewProps,
    'videoId' | 'graphicId' | 'assetImageUrl' | 'assetVideoUrl'
  >
): CreativeRef | null {
  if (ad.assetImageUrl || ad.assetVideoUrl) return null;
  if (ad.videoId) return { kind: 'video', id: ad.videoId };
  if (ad.graphicId) return { kind: 'graphic', id: ad.graphicId };
  return null;
}

export interface AdArtifactRowProps {
  /** The ad as the tool reported it; the saved record overrides it once read. */
  preview: AdPreviewProps & { adId: string };
  /**
   * Never show this row as selected — a later card in the transcript shows
   * the same ad, and only the newest should light up when it is open.
   */
  suppressSelectedHighlight?: boolean;
}

/**
 * A saved ad, as a row in the conversation.
 *
 * The same shape a finished video or graphic takes: a thumbnail, a name, a
 * one-line status, and the whole row opens the ad at full size in the side
 * panel. The card this replaces embedded the creative and every line of copy
 * in the transcript, so three ads in a row were the tallest thing on the page
 * and none of them could be opened anywhere else.
 *
 * It POLLS THE CREATIVE, not the ad. The ad row is written in the same turn
 * that starts the graphic or video render, so the ad exists before its
 * picture does; the row pulses until the worker reports the creative ready,
 * then shows the still. The poll shares its query key with the panel, so one
 * request feeds both and they never disagree about whether it has finished.
 *
 * Terminal states are explicit so a skeleton can never outlive its reason:
 * a failed render says so in the row, a creative the server cannot return
 * drops the thumbnail rather than pulsing forever, and an uploaded asset is
 * never pending at all.
 */
export function AdArtifactRow({
  preview,
  suppressSelectedHighlight,
}: AdArtifactRowProps) {
  const { autoOpenArtifact } = useArtifactPanel();
  const ad = useSavedAdPreview(preview);
  const creative = adCreativeToPoll(ad);

  const { asset, pending, failed, observedRendering } =
    useCreativeAsset(creative);
  const firstSlide = creativeSlides(asset)[0];
  const thumbnailUrl =
    creative === null
      ? (ad.assetThumbnailUrl ?? ad.assetImageUrl)
      : creative.kind === 'video'
        ? asset?.thumbnailUrl
        : (firstSlide?.thumbnailUrl ?? firstSlide?.url);

  // Open the panel as soon as the creative is SEEN rendering, once.
  //
  // Not when it lands, as a graphic does: the graphic card that started the
  // render opens the panel on landing too, so an ad that also waited for the
  // landing arrived second and REPLACED the graphic — the panel remounted
  // under the owner's eyes, the "reload" between two versions of the same
  // picture. Opening on the ad while it is still rendering means the panel
  // is already on the ad when the picture arrives, the picture fills its
  // frame in place, and the graphic card defers to an open ad.
  //
  // Only a row that is actually WAITING opens itself — the first poll has to
  // report an unfinished status. An ad whose creative was ready from the start
  // (an upload, or a graphic made earlier in the conversation) is opened by a
  // click, so reloading an old conversation does not throw every finished ad
  // in it at the panel in turn.
  const openedRef = useRef(false);
  const creativeId = creative?.id;
  useEffect(() => {
    if (observedRendering && !suppressSelectedHighlight && !openedRef.current) {
      openedRef.current = true;
      autoOpenArtifact({ kind: 'ad', id: preview.adId, creativeId });
    }
  }, [
    observedRendering,
    suppressSelectedHighlight,
    autoOpenArtifact,
    preview.adId,
    creativeId,
  ]);

  if (failed) {
    const reason =
      creative?.kind === 'graphic'
        ? describeGraphicFailure(asset ?? {}, 'Ask Claire to try it again.')
            .message
        : (asset?.errorMessage ?? 'Ask Claire to try it again.');
    return (
      <div
        role="alert"
        data-testid="claire-card-ad-artifact-failed"
        className="flex w-full items-center gap-3 rounded-xl border border-destructive/40 bg-card p-2.5 sm:max-w-sm"
      >
        <span className="grid size-11 shrink-0 place-items-center rounded-lg border bg-muted">
          <AlertTriangle className="size-5 text-destructive" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">
            {ad.adName}
          </span>
          <span className="block text-xs text-muted-foreground">
            The {creative?.kind === 'video' ? 'video' : 'graphic'} for this ad
            couldn&apos;t be rendered. {reason}
          </span>
        </span>
      </div>
    );
  }

  const status = describeAdStatus(ad);
  const subtitle = pending
    ? `Ad · Creating ${creative?.kind === 'video' ? 'video' : 'graphic'}…`
    : [`Ad · ${status.label}`, ad.campaignName].filter(Boolean).join(' · ');

  return (
    <div data-testid="claire-card-ad-artifact" className="w-full sm:max-w-sm">
      <ArtifactRow
        artifact={{ kind: 'ad', id: preview.adId, creativeId }}
        title={ad.adName}
        subtitle={subtitle}
        thumbnailUrl={thumbnailUrl}
        pending={pending}
        disableSelectedHighlight={suppressSelectedHighlight}
      />
    </div>
  );
}
